import { useCallback } from "react";
import { type GridRowId } from "@mui/x-data-grid-pro";
import { useApiClient } from "../../../hooks/useApiClient";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";

interface ZipDownloadUrlAPIResponse {
  /** CREATED: 作成済み（zipFileUriにURLが入る） / CREATING: 作成中 */
  status: "CREATED" | "CREATING";
  zipFileUri: string | null;
}

const showDownloadErrorToast = (): void => {
  toast.error(
    <>
      ダウンロードに失敗しました
      <br />
      時間をおいて再度お試しください。
    </>
  );
};

/**
 * 商業登記取得結果の一括ダウンロード用ZIPのURLを取得し、ダウンロードを開始するhook
 *
 * ZIPは取得完了後に非同期で作成されるため、状態に応じて以下を表示する。
 * - 作成済み（status: CREATED）: 別タブでダウンロードを開始する
 * - 作成中（status: CREATING）: 作成中である旨をトーストで表示する
 * - 対象なし（HTTP 404）: ダウンロードできるファイルがない旨をトーストで表示する
 *
 * NOTE: 本EPは既存リクエストの現在状態を返すGETのため、作成中もHTTPは200を返す。
 *       作成状態はレスポンスボディのstatusで判定する。
 */
export const useCommercialZipDownloadUrl = (): {
  handleDownload: (id: GridRowId) => Promise<void>;
} => {
  const { apiClient } = useApiClient();
  const handleDownload = useCallback(
    async (id: GridRowId): Promise<void> => {
      try {
        const response = await apiClient.get<ZipDownloadUrlAPIResponse>(
          `/commercial-picture-request/${id}/zip-download-url`
        );

        const { status, zipFileUri } = response.data;
        if (status === "CREATING") {
          toast.info(
            <>
              一括ダウンロード用のファイルを作成中です。
              <br />
              これには数分かかる場合があります。
            </>
          );
          return;
        }

        // NOTE: localstack環境ではhttpになるため、両方を許容する
        if (
          zipFileUri !== null &&
          (zipFileUri.startsWith("https://") ||
            zipFileUri.startsWith("http://"))
        ) {
          window.open(zipFileUri, "_blank", "noopener,noreferrer");
          return;
        }

        // NOTE: CREATEDで必ずURLが返るのがバックエンドとの契約のため、想定外の値は通知する
        Sentry.captureMessage(
          `ZIPダウンロードURLの想定外レスポンス: status=${status}, zipFileUri=${String(
            zipFileUri
          )}`,
          "warning"
        );
        showDownloadErrorToast();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          toast.warning("ダウンロードできるファイルがありません。");
          return;
        }
        Sentry.captureException(error);
        showDownloadErrorToast();
      }
    },
    [apiClient]
  );

  return { handleDownload };
};
